import { Heading, Icon, Text } from '@ui/themed';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import { cn, SHIMMER_CLASSES } from '../../../lib/styles';
import type { IconSize } from '../icon/types';
import type { TextSize } from '../text/types';
import type {
  ProgressBarProps,
  ProgressBarSegmentLabels,
  ProgressBarSize,
  ProgressBarTitle,
} from './types';

/** Platform-supplied accent colours: the fill (completed) and active-segment classNames. */
export type ProgressBarAccent = { fill: string; active: string };

// Everything that scales with the `size` prop, kept together so the sizes stay in step.
const BAR_HEIGHT: Record<ProgressBarSize, string> = {
  sm: 'h-1',
  md: 'h-1.5',
  lg: 'h-2.5',
};
const LABEL_ICON_SIZE: Record<ProgressBarSize, IconSize> = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
};
const LABEL_TEXT_SIZE: Record<ProgressBarSize, TextSize> = {
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

// Heading typography defaults, applied when `title` is a bare string or omits them. Deliberately
// fixed (not derived from the bar's `size`) so the heading and bar sizes stay independent.
const HEADING_STYLE_DEFAULT = 'md';
const HEADING_LEVEL_DEFAULT = 'h2';

const TRACK_CLASSES = 'rounded-full bg-gray-200';

// Single track with a width-driven fill — the default rendering.
function ContinuousBar({
  value,
  label,
  size,
  fillClassName,
}: {
  value: number;
  label?: string;
  size: ProgressBarSize;
  fillClassName: string;
}) {
  const pct = Number.isNaN(value) ? 0 : Math.max(0, Math.min(100, value));

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn(TRACK_CLASSES, BAR_HEIGHT[size], 'w-full overflow-hidden')}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-300 ease-out motion-reduce:transition-none',
          fillClassName,
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// A row of equal-width rounded pills; the leading `filled` of them carry the accent, and — when
// `highlightActive` is set — the first incomplete one is tinted distinctly. When `segmentLabels`
// are given, an icon+text label sits under each segment, styled by that segment's state.
function SegmentedBar({
  segments,
  filled,
  label,
  size,
  highlightActive,
  labels,
  fillClassName,
  activeClassName,
}: {
  segments: number;
  filled: number;
  label?: string;
  size: ProgressBarSize;
  highlightActive?: boolean;
  labels?: ProgressBarSegmentLabels;
  fillClassName: string;
  activeClassName: string;
}) {
  const count = Math.max(1, Math.floor(segments));
  const lit = Math.max(0, Math.min(count, Math.round(filled)));

  // The active segment is the first incomplete one (there isn't one once everything's filled).
  const activeIndex = lit < count ? lit : -1;

  const pills = (
    <div
      role="progressbar"
      aria-valuenow={lit}
      aria-valuemin={0}
      aria-valuemax={count}
      aria-label={label}
      className="flex w-full gap-1.5"
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length positional list, no reordering
          key={i}
          className={cn(
            TRACK_CLASSES,
            BAR_HEIGHT[size],
            'flex-1 transition-colors duration-300 ease-out motion-reduce:transition-none',
            i < lit && fillClassName,
            highlightActive && i === activeIndex && activeClassName,
          )}
        />
      ))}
    </div>
  );

  if (!labels) {
    return pills;
  }

  const { items } = labels;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full gap-1.5">
        {/* Keyed off `count`, not `items`, to stay 1:1 with the pills below; `items` may be
            shorter, leaving label slots empty (guarded on `item` before render). */}
        {Array.from({ length: count }, (_, i) => {
          const item = items[i];
          const isComplete = i < lit;
          // Completed and active labels render normally; only pending ones are dimmed.
          const isActive = i === activeIndex;
          const isPending = !isComplete && !isActive;
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length positional list, no reordering
              key={i}
              className={cn(
                'flex flex-1 items-center justify-center gap-1',
                isPending && 'opacity-50',
              )}
            >
              {item && (
                <>
                  {item.icon && <Icon name={item.icon} size={LABEL_ICON_SIZE[size]} />}
                  <Text size={LABEL_TEXT_SIZE[size]} variant="secondary">
                    {item.text}
                  </Text>
                  {isComplete && labels.markCompleted && (
                    <Icon name="check" size={LABEL_ICON_SIZE[size]} />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      {pills}
    </div>
  );
}

// Wraps a bar in the optional title + caption header. With neither, the bar is returned bare. The
// caption right-aligns to the bar's trailing edge and never wraps.
function withTitle(
  bar: ReactNode,
  { title, caption }: { title?: ProgressBarTitle; caption?: string },
) {
  if (!title && !caption) {
    return bar;
  }

  // Normalise the string shorthand to the object form so rendering has one shape to read.
  const heading = typeof title === 'string' ? { text: title } : title;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        {heading != null && (
          <Heading
            level={heading.level ?? HEADING_LEVEL_DEFAULT}
            style={heading.style ?? HEADING_STYLE_DEFAULT}
          >
            {heading.text}
          </Heading>
        )}
        {caption != null && (
          <Text size="sm" variant="secondary">
            <span className="shrink-0 whitespace-nowrap tabular-nums">{caption}</span>
          </Text>
        )}
      </div>
      {bar}
    </div>
  );
}

/**
 * Progress bar — a continuous track+fill (`value`), or a row of discrete pills (`segments` +
 * `filled`). The segmented variant can be headed by a title + "x of y" caption, or instead label
 * each segment with an icon+text pair. The geometry and animation are shared; each platform frame
 * supplies its own `accent` (fill + active colours) so it matches that platform's design language.
 */
export function ProgressBarBase(props: ProgressBarProps & { accent: ProgressBarAccent }) {
  const { size = 'md', accent } = props;

  if (props.segments === undefined) {
    return withTitle(
      <ContinuousBar
        value={props.value}
        label={props.label}
        size={size}
        fillClassName={accent.fill}
      />,
      { title: props.title },
    );
  }

  // Segmented bar with per-segment labels — these stand in for the header.
  if (props.segmentLabels !== undefined) {
    return (
      <SegmentedBar
        segments={props.segments}
        filled={props.filled}
        size={size}
        highlightActive={props.highlightActive}
        labels={props.segmentLabels}
        fillClassName={accent.fill}
        activeClassName={accent.active}
      />
    );
  }

  // Segmented bar with the optional title + "x of y" caption header.
  const count = Math.max(1, Math.floor(props.segments));
  const filled = Math.max(0, Math.min(count, Math.round(props.filled)));
  const caption = props.caption
    ? t('components:progress_bar.count', { filled, total: count })
    : undefined;

  return withTitle(
    <SegmentedBar
      segments={props.segments}
      filled={props.filled}
      label={props.label}
      size={size}
      highlightActive={props.highlightActive}
      fillClassName={accent.fill}
      activeClassName={accent.active}
    />,
    { title: props.title, caption },
  );
}

export type ProgressBarSkeletonProps = {
  /** Render a title placeholder above the bar. */
  title?: boolean;
  /** Render a trailing caption placeholder beside the title. */
  caption?: boolean;
  /** Render this many pill placeholders instead of one continuous track — matches the segmented bar. */
  segments?: number;
  /** Bar thickness. Defaults to `md`. */
  size?: ProgressBarSize;
};

// Decorative placeholder matching the bar's geometry — segmented when `segments` is set, and, when
// `title`/`caption` are set, the header row above it. Left out of the a11y tree — the surrounding
// loading region owns the busy state.
export function ProgressBarSkeleton({
  title = false,
  caption = false,
  segments,
  size = 'md',
}: ProgressBarSkeletonProps = {}) {
  const height = BAR_HEIGHT[size];
  const track =
    segments && segments > 0 ? (
      <div className="flex w-full gap-1.5">
        {Array.from({ length: Math.max(1, Math.floor(segments)) }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length positional list, no reordering
          <div key={i} className={cn(height, 'flex-1 rounded-full', SHIMMER_CLASSES)} />
        ))}
      </div>
    ) : (
      <div className={cn(height, 'w-full rounded-full', SHIMMER_CLASSES)} />
    );

  if (!title && !caption) {
    return <div aria-hidden>{track}</div>;
  }

  return (
    <div aria-hidden className="flex w-full flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        {title && <span className={cn('h-5 w-40 rounded-full', SHIMMER_CLASSES)} />}
        {caption && <span className={cn('h-4 w-12 rounded-full', SHIMMER_CLASSES)} />}
      </div>
      {track}
    </div>
  );
}
